import { useEventDetails } from 'hooks/user/useEvents'
import { useMemo, useState } from 'react'
import { EventDetails, EventType } from 'types/Event'

export const useEventSelector = (currentEvent: EventDetails, events: EventType[]) => {
    const [selectedEventId, setSelectedEventId] = useState<string | null>(currentEvent?.event_id || null)
    const [selectedLocation, setSelectedLocation] = useState<string | null>(currentEvent?.location_id || null)
    const [showDetails, setShowDetails] = useState(false)

    const filteredEvents = useMemo(() => {
        const now = new Date()
        return events.filter((event) => event.show_id === currentEvent.show_id && new Date(event.event_date) >= now)
    }, [events, currentEvent.show_id])

    const { eventDetails } = useEventDetails(filteredEvents)

    const selectedEventData = selectedEventId ? eventDetails[selectedEventId] : null

    const locationOptions = useMemo(() => {
        const locationsMap = new Map<string, string>()

        const now = new Date()

        filteredEvents.forEach((event) => {
            const details = eventDetails[event.event_id]
            const location = details?.location
            const eventDate = details?.event_date ? new Date(details.event_date) : null

            // keep only if location exists and event is in the future
            if (location && eventDate && eventDate >= now) {
                locationsMap.set(event.location_id, location.name)
            }
        })

        return Array.from(locationsMap.entries()).map(([id, name]) => ({
            value: id,
            label: name,
        }))
    }, [filteredEvents, eventDetails])

    const filteredEventsByLocation = useMemo(
        () =>
            selectedLocation
                ? filteredEvents.filter((event) => eventDetails[event.event_id]?.location?.location_id === selectedLocation)
                : filteredEvents,
        [filteredEvents, eventDetails, selectedLocation],
    )

    const handleToggleDetails = () => {
        setShowDetails((prev) => !prev)
        if (!showDetails) {
            setSelectedLocation(currentEvent.location_id)
        }
    }

    return {
        selectedEventId,
        setSelectedEventId,
        selectedLocation,
        setSelectedLocation,
        showDetails,
        handleToggleDetails,
        filteredEventsByLocation,
        selectedEventData,
        locationOptions,
        eventDetails,
    }
}
